/** Openclaw Community — Core Type Definitions & UI Constants */

// Re-export all pure types from the shared types package
export type {
  AbilityType,
  BadgeCategory,
  BadgeRarity,
  Badge,
  ActivityEventType,
  ActivityEvent,
  AbilityGroup,
  CreatorEarnings,
  SocialLink,
  UserBoardActivity,
  ProfileTab,
  FeedTab,
  CommunityUser,
  CommunityPost,
  CommunityComment,
  CommunityBoard,
  CommunityFeedResponse,
  PublishDraft,
  VipTier,
  VipPlan,
  VipStatus,
  VipOrderResult,
} from "@zrt-ai-office/types";

import type {
  AbilityType,
  ProfileTab,
  CommunityPost,
  CommunityUser,
} from "@zrt-ai-office/types";

/* ── Ability type UI config (Tailwind classes, colors) ── */
export const ABILITY_TYPE_CONFIG = {
  skill: {
    label: "技能",
    labelZh: "技能",
    hex: "#3B82F6",
    bgClass: "bg-blue-500/80",
    textClass: "text-white",
    outlineTextClass: "text-blue-400",
    hoverBgClass: "hover:bg-blue-500/80",
    borderClass: "border-blue-500/30",
    joinBgClass: "bg-blue-500 hover:bg-blue-400",
    joinDoneBgClass: "bg-blue-500/10 text-blue-500",
    icon: "Zap",
    joinLabel: "加入我的技能",
  },
  tool: {
    label: "工具",
    labelZh: "工具",
    hex: "#F59E0B",
    bgClass: "bg-amber-500/80",
    textClass: "text-white",
    outlineTextClass: "text-amber-400",
    hoverBgClass: "hover:bg-amber-500/80",
    borderClass: "border-amber-500/30",
    joinBgClass: "bg-amber-500 hover:bg-amber-400",
    joinDoneBgClass: "bg-amber-500/10 text-amber-500",
    icon: "Wrench",
    joinLabel: "加入我的工具",
  },
  workflow: {
    label: "工作流",
    labelZh: "工作流",
    hex: "#10B981",
    bgClass: "bg-emerald-500/80",
    textClass: "text-white",
    outlineTextClass: "text-emerald-400",
    hoverBgClass: "hover:bg-emerald-500/80",
    borderClass: "border-emerald-500/30",
    joinBgClass: "bg-emerald-500 hover:bg-emerald-400",
    joinDoneBgClass: "bg-emerald-500/10 text-emerald-500",
    icon: "GitBranch",
    joinLabel: "加入我的工作流",
  },
  agent: {
    label: "智能体",
    labelZh: "智能体",
    hex: "#1D4ED8",
    bgClass: "bg-blue-700/80",
    textClass: "text-white",
    outlineTextClass: "text-blue-400",
    hoverBgClass: "hover:bg-blue-700/80",
    borderClass: "border-blue-700/30",
    joinBgClass: "bg-blue-700 hover:bg-blue-600",
    joinDoneBgClass: "bg-blue-700/10 text-blue-700",
    icon: "Bot",
    joinLabel: "加入我的智能体",
  },
  "agent-team": {
    label: "智能体团队",
    labelZh: "智能体团队",
    hex: "#EF4444",
    hexEnd: "#EC4899",
    bgClass: "bg-red-500/80",
    textClass: "text-white",
    outlineTextClass: "text-red-400",
    hoverBgClass: "hover:bg-red-500/80",
    borderClass: "border-red-500/30",
    joinBgClass: "bg-red-500 hover:bg-red-400",
    joinDoneBgClass: "bg-red-500/10 text-red-500",
    icon: "Users",
    joinLabel: "加入我的团队",
  },
  discussion: {
    label: "交流",
    labelZh: "交流",
    hex: "#6B7280",
    bgClass: "bg-gray-500/80",
    textClass: "text-white",
    outlineTextClass: "text-gray-400",
    hoverBgClass: "hover:bg-gray-500/80",
    borderClass: "border-gray-500/30",
    joinBgClass: "bg-gray-500 hover:bg-gray-400",
    joinDoneBgClass: "bg-gray-500/10 text-gray-500",
    icon: "MessageCircle",
    joinLabel: "",
  },
} as const satisfies Record<
  AbilityType,
  {
    label: string;
    labelZh: string;
    hex: string;
    hexEnd?: string;
    bgClass: string;
    textClass: string;
    outlineTextClass: string;
    hoverBgClass: string;
    borderClass: string;
    joinBgClass: string;
    joinDoneBgClass: string;
    icon: string;
    joinLabel: string;
  }
>;

/* ── Creator level system (6 tiers) ── */
export const CREATOR_LEVELS = [
  {
    level: 1,
    title: "Claw新丁",
    subtitle: "刚踏上江湖，但身怀潜力",
    icon: "seedling" as const,
    color: "oklch(0.55 0.08 160)",
    colorEnd: "oklch(0.50 0.10 180)",
    textClass: "text-muted-foreground",
    bgClass: "bg-muted/60",
  },
  {
    level: 2,
    title: "调参侠",
    subtitle: "别人用默认，你爱折腾",
    icon: "wrench" as const,
    color: "oklch(0.58 0.16 240)",
    colorEnd: "oklch(0.52 0.18 260)",
    textClass: "text-blue-500",
    bgClass: "bg-blue-500/10",
  },
  {
    level: 3,
    title: "Workflow 猎人",
    subtitle: "工作流是你的语言，效率是你的信仰",
    icon: "zap" as const,
    color: "oklch(0.60 0.17 155)",
    colorEnd: "oklch(0.50 0.19 170)",
    textClass: "text-emerald-500",
    bgClass: "bg-emerald-500/10",
  },
  {
    level: 4,
    title: "Agent 架构师",
    subtitle: "你搭的不是工具，是一套思考系统",
    icon: "brain" as const,
    color: "oklch(0.72 0.16 65)",
    colorEnd: "oklch(0.62 0.18 45)",
    textClass: "text-amber-500",
    bgClass: "bg-amber-500/10",
  },
  {
    level: 5,
    title: "造物主",
    subtitle: "你创造的AI能力，正在帮助真实的人",
    icon: "flame" as const,
    color: "oklch(0.65 0.22 30)",
    colorEnd: "oklch(0.58 0.24 10)",
    textClass: "text-red-500",
    bgClass: "bg-red-500/10",
  },
  {
    level: 6,
    title: "Openclaw 传说",
    subtitle: "这个社区因为有你而不一样",
    icon: "crown" as const,
    color: "oklch(0.75 0.14 70)",
    colorEnd: "oklch(0.62 0.16 20)",
    textClass: "text-amber-400",
    bgClass: "bg-amber-400/10",
  },
] as const;

/* ── Creator level visual effects ── */
export const CREATOR_LEVEL_VISUALS = [
  {
    level: 1,
    glowColor: "transparent",
    ringClass: "ring-1 ring-muted-foreground/20",
  },
  {
    level: 2,
    glowColor: "rgba(59,130,246,0.3)",
    ringClass: "ring-2 ring-blue-400/40",
  },
  {
    level: 3,
    glowColor: "rgba(16,185,129,0.4)",
    ringClass: "ring-2 ring-emerald-400/50",
    animation: "shimmer",
  },
  {
    level: 4,
    glowColor: "rgba(245,158,11,0.4)",
    ringClass: "ring-2 ring-amber-400/50",
    animation: "shimmer",
  },
  {
    level: 5,
    glowColor: "rgba(239,68,68,0.5)",
    ringClass: "ring-2 ring-red-400/60",
    animation: "pulse-glow",
  },
  {
    level: 6,
    glowColor: "rgba(245,180,60,0.55)",
    ringClass: "ring-2 ring-amber-300/70",
    animation: "rainbow-glow",
  },
] as const;

/* ── Profile tabs UI config ── */
export const PROFILE_TABS: {
  key: ProfileTab;
  label: string;
  colorClass?: string;
}[] = [
  { key: "all", label: "全部" },
  { key: "skill", label: "Skill", colorClass: "text-blue-500" },
  { key: "workflow", label: "Workflow", colorClass: "text-emerald-500" },
  { key: "tool", label: "Tool", colorClass: "text-amber-500" },
  { key: "agent", label: "Agent", colorClass: "text-blue-700" },
  { key: "agent-team", label: "智能体团队", colorClass: "text-red-500" },
  { key: "article", label: "图文" },
  { key: "favorites", label: "收藏" },
  { key: "activity", label: "动态" },
];

/* ── Copy count display ── */
export function formatCopyCount(count: number): string {
  if (count === 0) return "";
  if (count < 10) return `已有${count}位探路者复制`;
  if (count < 100) return `${count}人已抄走这份宝贝`;
  if (count < 1000) return `${count}人验证过了：真的好用`;
  if (count < 10000)
    return `🔥 ${(count / 1000).toFixed(1)}K 复制 · 社区爆款认证`;
  return `🏆 ${(count / 10000).toFixed(1)}W 复制 · Openclaw 传说级内容`;
}

/* ── Normalized types for entity stores ── */

/** 帖子内 author 拆为 authorId 引用 */
export interface NormalizedPost extends Omit<CommunityPost, "author"> {
  authorId: string;
}

/** Feed 查询参数，用于 buildFeedKey 和 loadFeed */
export interface FeedParams {
  tab?: "recommended" | "following" | "hot" | "latest";
  tag?: string;
  boardSlug?: string;
  userId?: string;
  abilityType?: AbilityType;
  search?: string;
  sort?: "hot" | "new" | "top" | "likes";
  sortDirection?: "desc" | "asc";
  page?: number;
  pageSize?: number;
}

/** 归一化单帖子结果 */
export interface NormalizedPostResult {
  post: NormalizedPost;
  author: CommunityUser;
}

/** 归一化帖子列表结果（不含分页信息） */
export interface NormalizedPostListResult {
  posts: NormalizedPost[];
  authors: CommunityUser[];
}

/** Feed 切片，存储在 feed-store 中 */
export interface FeedSlice {
  postIds: string[];
  total: number;
  page: number;
  hasMore: boolean;
  staleAt: number;
}
